/**
 * ORDER Health Check — one short frequency check per core seat. 8 broad
 * questions; if they show dysfunction, branch into each candidate malady's
 * specific questions and narrow to the most likely one. Saves through
 * saveIntake() so the seat's spectrum is seeded from the result.
 */
import React, { useState } from 'react';
import { Image, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { supabase } from '../lib/supabase';
import { DISPLAY_FONT, makeThemedStyles, useTheme } from '../v10/theme';
import { Atmos, GlassCard, Kicker, Medallion } from '../v10/ui';
import { GEAR_LABEL } from '../v10/roster';
import type { GearId } from './ascension';
import { saveIntake } from './spectrum-data';
import { getTool } from './spectrum-tools';
import { maladyArt } from './deityArt';
import { BROAD, FREQ_OPTIONS, SEAT_BY_GEAR, SPECIFIC, needsBranch, scoreSeat, type SeatResult } from './moyriSeats';

type Phase = { kind: 'intro' } | { kind: 'broad'; i: number } | { kind: 'specific'; i: number } | { kind: 'result'; result: SeatResult };

export function MoyriHealthCheck({
  gears,
  userId,
  onClose,
}: {
  gears: GearId[];
  userId: string | undefined;
  onClose: () => void;
}) {
  const styles = useStyles();
  const [g, setG] = useState(0);
  const [phase, setPhase] = useState<Phase>({ kind: 'intro' });
  const [broad, setBroad] = useState<number[]>([]);
  const [spec, setSpec] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState<SeatResult[]>([]);

  const gear = gears[g];
  const seat = gear ? SEAT_BY_GEAR[gear] : undefined;
  const specificItems = seat ? seat.maladies.flatMap((m) => SPECIFIC[m].map((text) => ({ m, text }))) : [];

  const finishSeat = async (broadAnswers: number[], specAnswers?: number[]) => {
    if (!seat || !gear) return;
    let byMalady: Record<string, number[]> | undefined;
    if (specAnswers) {
      byMalady = {};
      specificItems.forEach((it, i) => {
        (byMalady![it.m] ??= []).push(specAnswers[i] ?? 0);
      });
    }
    const result = scoreSeat(gear, broadAnswers, byMalady);
    setSaving(true);
    if (userId) {
      await saveIntake(
        supabase,
        userId,
        seat.toolId,
        { kind: 'order-health-check', version: 1, gear, broad: broadAnswers, specific: byMalady ?? null, result },
        result.maladyId,
        result.position,
      );
    }
    setSaving(false);
    setDone((d) => [...d, result]);
    setPhase({ kind: 'result', result });
  };

  const answer = (v: number) => {
    if (saving || !gear) return;
    if (phase.kind === 'broad') {
      const next = [...broad, v];
      setBroad(next);
      if (phase.i + 1 < BROAD[gear].length) return setPhase({ kind: 'broad', i: phase.i + 1 });
      if (needsBranch(gear, next)) {
        setSpec([]);
        return setPhase({ kind: 'specific', i: 0 });
      }
      return finishSeat(next);
    }
    if (phase.kind === 'specific') {
      const next = [...spec, v];
      setSpec(next);
      if (phase.i + 1 < specificItems.length) return setPhase({ kind: 'specific', i: phase.i + 1 });
      return finishSeat(broad, next);
    }
  };

  const nextSeat = () => {
    setBroad([]);
    setSpec([]);
    if (g + 1 < gears.length) {
      setG(g + 1);
      setPhase({ kind: 'intro' });
    } else {
      setG(gears.length);
    }
  };

  // ── Summary after the last seat ──
  if (!gear || !seat) {
    return (
      <Atmos>
        <ScrollView contentContainerStyle={styles.wrap}>
          <Kicker style={styles.kicker}>ORDER HEALTH CHECK · DONE</Kicker>
          <Text style={styles.title}>Who holds each seat</Text>
          {done.map((r) => (
            <ResultRow key={r.gear} result={r} />
          ))}
          <Text style={styles.note}>
            Every seat a malady holds now has an active SWAP. Real tool use moves the line from here.
          </Text>
          <Pressable onPress={onClose} style={styles.primary}>
            <Text style={styles.primaryText}>See my SWAPs</Text>
          </Pressable>
        </ScrollView>
      </Atmos>
    );
  }

  const seatLabel = `${GEAR_LABEL[gear].name.toUpperCase()} · ${seat.name}`;
  const progress = gears.length > 1 ? `seat ${g + 1} of ${gears.length}` : 'one seat';

  return (
    <Atmos>
      <ScrollView contentContainerStyle={styles.wrap}>
        <View style={styles.topRow}>
          <Kicker style={styles.kicker}>
            {seatLabel} · {progress}
          </Kicker>
          <Pressable onPress={onClose} hitSlop={10}>
            <Text style={styles.close}>✕</Text>
          </Pressable>
        </View>

        {phase.kind === 'intro' ? (
          <>
            <Medallion id={seat.toolId} size={96} />
            <Text style={styles.title}>{GEAR_LABEL[gear].question}</Text>
            <Text style={styles.body}>
              {BROAD[gear].length} quick questions about the last few weeks. This finds out whether {seat.name} holds{' '}
              {GEAR_LABEL[gear].name}, or a malady does.
            </Text>
            <Pressable onPress={() => setPhase({ kind: 'broad', i: 0 })} style={styles.primary}>
              <Text style={styles.primaryText}>Start</Text>
            </Pressable>
          </>
        ) : phase.kind === 'result' ? (
          <>
            <ResultRow result={phase.result} big />
            <Text style={styles.body}>
              {phase.result.holder === 'deity'
                ? `${seat.name} holds ${GEAR_LABEL[gear].name}. No SWAP needed — level up ${seat.name} in training.`
                : `A SWAP is built: ${maladyName(seat.toolId, phase.result.maladyId)} → ${seat.name}. MoyRi now watches this seat.`}
            </Text>
            <Pressable onPress={nextSeat} style={styles.primary}>
              <Text style={styles.primaryText}>{g + 1 < gears.length ? 'Next seat' : 'Finish'}</Text>
            </Pressable>
          </>
        ) : (
          <>
            <Text style={styles.count}>
              {phase.kind === 'broad'
                ? `${phase.i + 1} / ${BROAD[gear].length}`
                : `narrowing it down · ${phase.i + 1} / ${specificItems.length}`}
            </Text>
            <Text style={styles.prompt}>
              {phase.kind === 'broad' ? BROAD[gear][phase.i].text : specificItems[phase.i].text}
            </Text>
            <View style={styles.options}>
              {FREQ_OPTIONS.map((label, v) => (
                <Pressable key={label} disabled={saving} onPress={() => answer(v)}>
                  <GlassCard style={styles.option}>
                    <Text style={styles.optionText}>{label}</Text>
                  </GlassCard>
                </Pressable>
              ))}
            </View>
            <Text style={styles.note}>No grade, no shame — this marks where you are now.</Text>
          </>
        )}
      </ScrollView>
    </Atmos>
  );
}

function maladyName(toolId: string, maladyId: string): string {
  return getTool(toolId as Parameters<typeof getTool>[0]).maladies.find((m) => m.id === maladyId)?.name ?? maladyId;
}

function ResultRow({ result, big }: { result: SeatResult; big?: boolean }) {
  const { c } = useTheme();
  const styles = useStyles();
  const seat = SEAT_BY_GEAR[result.gear];
  const isDeity = result.holder === 'deity';
  const name = isDeity ? seat.name : maladyName(seat.toolId, result.maladyId);
  const art = maladyArt(result.maladyId, seat.toolId);
  const size = big ? 72 : 44;
  return (
    <GlassCard style={styles.resultRow}>
      {isDeity ? (
        <Medallion id={seat.toolId} size={size} />
      ) : art ? (
        <Image source={art} style={{ width: size, height: size, borderRadius: size / 2 }} />
      ) : null}
      <View style={{ flex: 1 }}>
        <Text style={styles.resultGear}>{GEAR_LABEL[result.gear].name.toUpperCase()}</Text>
        <Text style={[styles.resultName, { color: isDeity ? c.ink : c.danger }]}>
          {name} holds
        </Text>
        <Text style={styles.resultMeta}>
          {isDeity ? 'healthy' : `severity ${result.severity} · ${result.confidence} confidence`}
        </Text>
      </View>
    </GlassCard>
  );
}

const useStyles = makeThemedStyles(({ t, c }) =>
  StyleSheet.create({
    wrap: { flexGrow: 1, alignItems: 'center', justifyContent: 'center', gap: 18, padding: 24 },
    topRow: { width: '100%', maxWidth: 400, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
    kicker: { letterSpacing: 2 },
    close: { fontSize: 18, color: c.textFaint },
    title: { fontFamily: DISPLAY_FONT, fontSize: 22, color: t.ivory, textAlign: 'center' },
    body: { maxWidth: 380, fontSize: 14, lineHeight: 20, color: c.inkSoft, textAlign: 'center' },
    count: { fontSize: 11, letterSpacing: 2, color: c.textFaint },
    prompt: { maxWidth: 400, fontFamily: DISPLAY_FONT, fontSize: 20, lineHeight: 27, color: t.ivory, textAlign: 'center' },
    options: { width: '100%', maxWidth: 360, gap: 10 },
    option: { borderRadius: 14, paddingHorizontal: 16, paddingVertical: 13 },
    optionText: { fontSize: 15, color: t.ivory, textAlign: 'center' },
    note: { maxWidth: 360, fontSize: 11, color: t.ivoryFaint, textAlign: 'center' },
    primary: { borderRadius: 999, backgroundColor: t.gold, paddingHorizontal: 28, paddingVertical: 12 },
    primaryText: { fontSize: 14, fontWeight: '800', letterSpacing: 1, color: t.onGold },
    resultRow: { width: '100%', maxWidth: 400, flexDirection: 'row', alignItems: 'center', gap: 14, padding: 14, borderRadius: 16 },
    resultGear: { fontSize: 10, letterSpacing: 2, color: c.textFaint },
    resultName: { fontFamily: DISPLAY_FONT, fontSize: 17 },
    resultMeta: { fontSize: 12, color: c.inkSoft },
  }),
);
